import { useRouter } from 'next/dist/client/router';
import { LOGO_MANFRED_WHITE } from 'utils/logos';
import {
  WP_REST_API_Post,
  WP_REST_API_User,
  WP_REST_API_Attachment,
  WP_REST_API_Categories
} from 'wp-types';
import {
  StyledPostHeader,
  StyledHeaderContent,
  StyledHeaderTitle,
  StyledHeaderMetaTable,
  StyledHeaderThumb,
  StyledHeaderTags,
  PublishedEntry,
  MetaText,
  MetaValue,
  ShareButton,
  StyledTagsAndShare
} from './PostHeaderCard.styled';
import { computeReadTime } from 'utils/computeReadTime';
import { useI18n } from 'hooks/useI18n';
import { getPostCategoriesId } from '../utils/blogUtils';
import Button from 'components/shared/Button.styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare, faCalendar } from '@fortawesome/pro-light-svg-icons';
import { CategoryBadge } from '../cards/CategoryBadge';

export interface PostHeaderCardProps {
  post: WP_REST_API_Post;
  sortedCategories: {
    parent: WP_REST_API_Categories;
    children: WP_REST_API_Categories;
  };
  onShareClick: () => void;
}

const hiddenCategories = [
  'uncategorized',
  'uncategorised',
  'oculto',
  'sin-categorizar',
  'all',
  'todo-el-blog'
] as const;

function getPostAuthor(post: WP_REST_API_Post): WP_REST_API_User | undefined {
  const embedded = post._embedded?.author;
  return embedded?.[0] as WP_REST_API_User | undefined;
}

function getPostThumb(post: WP_REST_API_Post): string | undefined {
  const media = post._embedded?.['wp:featuredmedia']?.[0] as
    | WP_REST_API_Attachment
    | undefined;
  return media?.source_url;
}

export const PostHeaderCard: React.FC<PostHeaderCardProps> = ({
  post,
  sortedCategories,
  onShareClick
}) => {
  const { t } = useI18n('success-stories');
  const { locale } = useRouter();
  const author = getPostAuthor(post);
  const thumb = getPostThumb(post);
  const readDuration = computeReadTime(post.content.rendered);

  return (
    <StyledPostHeader>
      <StyledHeaderThumb>
        <img
          src={thumb ?? LOGO_MANFRED_WHITE}
          decoding="async"
          alt=""
          role="presentation"
        />
      </StyledHeaderThumb>
      <StyledHeaderContent>
        <StyledHeaderMetaTable>
          <PublishedEntry>
            <MetaText>
              <MetaValue>
                <FontAwesomeIcon icon={faCalendar} />{' '}
                {new Date(post.date).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric'
                })}
              </MetaValue>
            </MetaText>
          </PublishedEntry>
        </StyledHeaderMetaTable>

        <StyledHeaderTitle
          dangerouslySetInnerHTML={{ __html: post.title.rendered }}
        />

        <StyledTagsAndShare>
          <StyledHeaderTags>
            <CategoryBadge
              maxCategories={7}
              tagList={getPostCategoriesId(post, sortedCategories.parent) ?? []}
            />
          </StyledHeaderTags>
          <ShareButton>
            <Button sizes="sm" variant="tertiary" onClick={onShareClick}>
              <FontAwesomeIcon icon={faShare} />
              <span>{t('post.share')}</span>
            </Button>
          </ShareButton>
        </StyledTagsAndShare>
      </StyledHeaderContent>
    </StyledPostHeader>
  );
};

export default PostHeaderCard;
